/* global JSX */
import * as React from "react";
import * as taskService from "../../../models/services/taskService";
import { ISessionState } from "../../../models/session/sessionState";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { List } from "../../framework/List/List";
import { useEffect, useState } from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { TaskDataRowDialog } from "./TaskDataRowDialog";
import { AppUtils } from "source/framework/appUtils";
import { infiniteListDetailsViewPageSize, infiniteListPageSize } from "source/models/common/webConsts";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { Box, Stack } from "@mui/material";
import { ListStateIconLocked } from "source/components/framework/List/ListStateIconLocked";
import { ListStateIconAttachment } from "source/components/framework/List/ListStateIconAttachment";
import { ConfirmationDialogResult, TaskDataRowDataType, TaskTypeClassHidden } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { IListValueLineData } from "source/components/framework/List/ListLine";
import { TaskDataRowImportDialog } from "./TaskDataRowImportDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export const getTaskDataRowLine = (sessionState: ISessionState, iconsColIndex: number, taskDataRow: ITaskDataRow, taskType: ITaskEditTypeItem): IListValueLineData => {
    return {
        columns: taskType.lineCols.map((i, index) => {
            const uiValue = taskDataRow.getTaskDataRowColListUiValue(i.field, taskType);
            const text = uiValue.text;
            let title = text;
            let value: JSX.Element = null;
            if (i.dataType === TaskDataRowDataType.Signature && text) {
                title = "";
                value = <Box
                    component="img"
                    sx={{
                        width: "100%",
                        objectFit: "contain"
                    }}
                    src={`data:image/png;base64,${text}`}
                />;
            }
            const isLockedToOtherUser = taskDataRow.isLockedToOtherUser(sessionState.code);
            return {
                id: i.field,
                styleClass: i.class,
                column: i.field,
                content: index !== iconsColIndex
                    ? (value ?? text)
                    : <Stack direction="row" useFlexGap flexWrap="nowrap" overflow="hidden" alignItems="center" sx={{ width: "100%" }}>
                        <div
                            className="colValueContainer"
                            title={title}
                            style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: "calc(100% - " + ((isLockedToOtherUser ? 24 : 0) + (taskDataRow.hasAttachments ? 24 : 0)).toString(10) + "px)",
                                flexGrow: "1",
                                display: "flex"
                            }}>{value ?? text}</div>
                        <ListStateIconLocked locked={isLockedToOtherUser} />
                        <ListStateIconAttachment hasAttachments={taskDataRow.hasAttachments} />
                    </Stack>
            };
        }),
        id: taskDataRow.id
    };
};

export interface ITaskDataRowListProp {
    sessionState: ISessionState;
    isReadOnly: boolean;
    companyId: string;
    taskId: string;
    dataId: string;
    taskType: ITaskEditTypeItem;
    filterFields?: string;
    detailsViewMode?: boolean;
    height?: string;
    onSetToolButtons?: (buttons: React.ReactNode) => void;
    onSetCheckedIds?: (checkedIds: string[]) => void;
    onIsLoadingChange?: (isLoading: boolean) => void;
}

export interface ITaskDataRowListState {
    items: ITaskDataRow[];
    hasMore: boolean;
    page: number;
    intialized: boolean;
    selectedId: string;
    checkedIds: string[];
    iconsColIndex: number;
    multiSelectCheckBoxColIndex: number;
    showTaskDataRowDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
    totalRowTaskType: ITaskEditTypeItem;
    totalRow: ITaskDataRow;
    showImportTaskDataRows: boolean;
}

//lineTotalExpression
export const TaskDataRowList = (props: ITaskDataRowListProp) => {
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        if (!props.onIsLoadingChange) return;
        props.onIsLoadingChange(isLoading);
    }, [isLoading]);

    const [state, setState] = useState<ITaskDataRowListState>({
        items: [],
        hasMore: false,
        page: 1,
        intialized: false,
        selectedId: "",
        checkedIds: [],
        iconsColIndex: 0,
        multiSelectCheckBoxColIndex: 0,
        showTaskDataRowDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
        totalRowTaskType: null,
        totalRow: null,
        showImportTaskDataRows: false
    });

    const searchItems = async (page: number, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const isNewTask = props.taskId === Base.emptyGuid;
        if (isNewTask) {
            setState(old => ({
                ...old,
                intialized: true
            }));
            return;
        }
        const pageSize = !props.detailsViewMode ? infiniteListPageSize : infiniteListDetailsViewPageSize;
        setIsLoading(true);
        const result = await AppUtils.callService(() => taskService.getTaskDataRowListItems(props.companyId, props.taskId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, props.filterFields), false, true, () => setIsLoading(false));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        for (const item of items.items) {
            item.initializeCols(props.taskType);
        }
        let selectedId = items.selectedId;
        const checkedIds = items.checkedIds;
        let index = items.items.findIndex(i => i.id === selectedId);
        if (moveNext && index < items.items.length - 1) {
            index = index + 1;
            selectedId = items.items[index].id;
        }
        //Calculate total row
        let totalRowTaskType = state.totalRowTaskType;
        if (totalRowTaskType === null || totalRowTaskType.id !== props.taskType.id) {
            totalRowTaskType = TaskEditTypeItem.getTotalRowTaskEditTypeItem(props.taskType);
        }
        let totalRow = state.totalRow;
        if (totalRowTaskType.lineCols.length > 0) {
            totalRow = TaskEditTypeItem.getTotalRow(props.taskType, totalRowTaskType, items.items);
        } else {
            totalRow = null;
        }
        setState(old => ({
            ...old,
            intialized: true,
            items: items.items,
            hasMore: result.hasMore,
            page: !refreshList ? result.page : page,
            selectedId,
            checkedIds,
            editDialogId: setEditDialogData ? (moveNext && selectedId ? selectedId : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
            totalRowTaskType,
            totalRow
        }));
    };

    useEffect(() => {
        searchItems(1, true, false);
    }, [props.taskId, props.dataId, props.filterFields]);

    useEffect(() => {
        if (!props.taskType?.id) return;
        let multiSelectCheckBoxColIndex = -1;
        let iconsColIndex = 0;
        for (let i = 0; i < props.taskType.lineCols.length; i++) {
            if (props.taskType.lineCols[i].class && props.taskType.lineCols[i].class.indexOf(TaskTypeClassHidden.Web) > -1) continue;
            if (multiSelectCheckBoxColIndex < 0) {
                multiSelectCheckBoxColIndex = i;
            }
            iconsColIndex = i;
        }
        setState(old => ({
            ...old,
            items: [],
            iconsColIndex,
            multiSelectCheckBoxColIndex
        }));
    }, [props.taskType?.id ?? ""]);

    // #region Edit dialog
    const showEditDialog = (id: string) => {
        const isNew = id === Base.emptyGuid;
        const index = isNew ? 0 : state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        setState(old => ({
            ...old,
            showTaskDataRowDialog: true,
            selectedId: id,
            editDialogId: id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < state.items.length - 1 || state.hasMore
        }));
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) return;
        if (index >= state.items.length) {
            if (!state.hasMore) return;
            searchItems(state.page + 1, false, false, true);
        } else {
            setState(old => ({
                ...old,
                selectedId: state.items[index].id,
                editDialogId: state.items[index].id,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore
            }));
        }
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState(old => ({
            ...old,
            selectedId,
            checkedIds
        }));
        if (!props.onSetCheckedIds) return;
        props.onSetCheckedIds(checkedIds);
    };

    const handleEditDialogOk = () => {
        refreshList(false);
    };

    const handleEditDialogCancel = (doRefreshList: boolean) => {
        setState(old => ({
            ...old,
            showTaskDataRowDialog: false
        }));
        if (!doRefreshList) return;
        refreshList(false);
    };
    // #endregion Edit dialog    

    const remove = async (id: string): Promise<ApiSuccess> => {
        if (!id) return;
        // Call server
        const result = await AppUtils.callService(() => taskService.removeTaskDataRow(props.companyId, props.taskId, id));
        if (!result) return null;
        refreshList();
        return result;
    };
    
    const handleAdd = (e) => {
        e.stopPropagation();
        showEditDialog(Base.emptyGuid);
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        showEditDialog(state.selectedId);
    };

    const handleRemove = async (e) => {
        e.stopPropagation();
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRemoveTaskDataRow, false) === ConfirmationDialogResult.Yes) {
            remove(state.selectedId);
        }
    };

    const handlePrint = async (e) => {
        if (e) {
            e.stopPropagation();
        }
        if (!state.selectedId) return;
        const fileName = await AppUtils.callService(() => taskService.getTaskDataRowPrint(props.companyId, props.taskId, state.selectedId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    const showImportTaskDataRows = () => {
        setState(old => ({
            ...old,
            showImportTaskDataRows: true
        }));
    };

    const handleTaskDataRowDialogOk = () => {
        setState(old => ({
            ...old,
            showImportTaskDataRows: false
        }));
        refreshList(false);
    };

    const handleTaskDataRowDialogCancel = () => {
        setState(old => ({
            ...old,
            showImportTaskDataRows: false
        }));
    };

    const createListIconButtons = () => {
        return (<ListIconButtonsItem
            stopPropagation={true}
            addTooltip={Translations.AddRow}
            editTooltip={props.isReadOnly
                ? Translations.OpenRow
                : Translations.EditRow}
            removeTooltip={Translations.RemoveRow}
            printTooltip={Translations.PrintRow}
            addDisabled={props.isReadOnly}
            editDisabled={!state.selectedId}
            removeDisabled={props.isReadOnly || !state.selectedId}
            printDisabled={!state.selectedId}
            moreDisabled={props.isReadOnly}
            moreActions={[
                { title: Translations.ImportTaskDataRows, onClick: showImportTaskDataRows, disabled: props.isReadOnly },
            ]}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onRemove={handleRemove}
            onPrint={handlePrint}
        />);
    };

    useEffect(() => {
        if (props.onSetToolButtons) {
            props.onSetToolButtons(createListIconButtons());
        }
    }, [props.isReadOnly, props.companyId, props.taskId, state.selectedId]);

    const refreshList = async (setDialogData: boolean = true) => {
        await searchItems(state.page, false, true, false, setDialogData);
    };

    const handleOpenLine = (id: string) => {
        if (!id) return;
        setState(old => ({
            ...old,
            selectedId: id
        }));
        showEditDialog(id);
    };

    const totalRowHeight = 40;

    return (
        <>
            <VerticalContainer
                before={<>
                    {!props.onSetToolButtons &&
                        <ActionContainer
                            margin="0 0 1rem 0"
                            rightItems={createListIconButtons()}
                        />
                    }
                </>}
                main={<>
                    <Box className="jsonFormLineData" sx={{ height: "100%" }}>
                        <List
                            intialized={state.intialized}
                            isLoading={!props.onIsLoadingChange && isLoading}
                            multiSelect={!!props.onSetCheckedIds}
                            multiSelectCheckBoxColIndex={state.multiSelectCheckBoxColIndex}
                            hasMore={!props.detailsViewMode && state.hasMore}
                            height={props.height ?? (
                                !props.detailsViewMode
                                    ? "100%"
                                    : (state.totalRow
                                        ? "calc(100% - " + totalRowHeight + "px)"
                                        : "auto")
                            )}
                            localLoadingDisabled={!!props.onIsLoadingChange}
                            globalLoadingInUse={false}
                            noDataMessage={props.taskId === Base.emptyGuid ? Translations.TaskMustBeSavedBeforeNewDataRowsCanBeAdded : null}
                            header={{
                                columns: props.taskType.lineCols.map(i => {
                                    return { styleClass: "colTitleContainer " + i.class, column: i.field, content: i.title };
                                })
                            }}
                            lines={state.items.map((taskDataRow) => getTaskDataRowLine(props.sessionState, state.iconsColIndex, taskDataRow, props.taskType))}
                            totalLine={state.totalRow && state.totalRowTaskType
                                ? getTaskDataRowLine(props.sessionState, state.iconsColIndex, state.totalRow, state.totalRowTaskType)
                                : null}
                            sortColumn={""}
                            sortOrderIsAsc={true}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={handleSetSelectedIds}
                            onSearchLines={(_sortColumn: string, _sortOrderIsAsc: boolean, nextPage: boolean) => searchItems(!nextPage ? 1 : state.page + 1, !nextPage, false)}
                            onOpenLine={handleOpenLine}
                        />
                    </Box>
                </>}
                disableMainInitialHeight={props.detailsViewMode}
            />
            {state.showTaskDataRowDialog &&
                <TaskDataRowDialog
                    sessionState={props.sessionState}
                    isReadOnly={props.isReadOnly}
                    companyId={props.companyId}
                    taskId={props.taskId}
                    id={state.editDialogId}
                    taskType={props.taskType}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onPrint={() => handlePrint(null)}
                    onRefreshList={() => refreshList(false)}
                />
            }
            {state.showImportTaskDataRows &&
                <TaskDataRowImportDialog
                    companyId={props.companyId}
                    taskId={props.taskId}
                    taskType={props.taskType}
                    onCancel={handleTaskDataRowDialogCancel}
                    onOk={handleTaskDataRowDialogOk}
                />
            }
        </>
    );
};
